// pages/TravelExpense/TravelArrangementPanel.js
//
// Go-live parity (2026-09-24): audit 09-travel #4 — legacy "Request
// Pre-Arrangements" tab (frmTravelExpense.vb:359-619, 2840-2877). A
// traveler asks HR to book air / hotel / rental car for the whole group
// (one request per trip, ExpenseTravelArrangementRequest); HR is e-mailed.
// HR (travel.hr) books it and presses "Mark all requests as fulfilled".
//
// Backend: GET  TravelExpense/{id}/arrangement
//          POST TravelExpense/{id}/arrangement
//          POST TravelExpense/{id}/arrangement/fulfill

import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Input, Label, Button, Spinner, Alert, Badge } from "reactstrap";
import api from "../../api";
import { toCamel } from "./travelCase";

const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return "—"; } };
const fmtDt = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString(); } catch { return "—"; } };
const tomorrowIso = () => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const yesNo = (v) => (v ? "Required" : "Not required");
const partOf = (m, a, e) => (m ? "Morning" : a ? "Afternoon" : e ? "Evening" : "—");

const RequiredRadio = ({ name, value, onChange, disabled }) => (
    <div>
        {[[true, "Required"], [false, "Not required"]].map(([v, label]) => (
            <div className="form-check form-check-inline" key={label}>
                <Input type="radio" className="form-check-input" id={`${name}-${label}`} name={name}
                    checked={value === v} onChange={() => onChange(v)} disabled={disabled} />
                <Label className="form-check-label" for={`${name}-${label}`}>{label}</Label>
            </div>
        ))}
    </div>
);

const FlightLeg = ({ title, prefix, form, set, disabled }) => (
    <Col md={6}>
        <div className="border rounded p-2 h-100">
            <div className="fw-semibold mb-2">{title}</div>
            <Label className="mb-1">Departure date</Label>
            <Input type="date" min={tomorrowIso()} value={form[`${prefix}DepartureDate`]}
                onChange={(e) => set(`${prefix}DepartureDate`, e.target.value)} disabled={disabled} />
            <div className="mt-2">
                {["morning", "afternoon", "evening"].map((p) => (
                    <div className="form-check form-check-inline" key={p}>
                        <Input type="radio" className="form-check-input" id={`${prefix}-${p}`} name={`${prefix}-part`}
                            checked={form[`${prefix}Part`] === p} onChange={() => set(`${prefix}Part`, p)} disabled={disabled} />
                        <Label className="form-check-label text-capitalize" for={`${prefix}-${p}`}>{p}</Label>
                    </div>
                ))}
            </div>
            <Label className="mb-1 mt-2">Departure time (preferred time range)</Label>
            <Input value={form[`${prefix}TimeRange`]} maxLength={200} onChange={(e) => set(`${prefix}TimeRange`, e.target.value)} disabled={disabled} />
            <Label className="mb-1 mt-2">Preferred airport</Label>
            <Input value={form[`${prefix}Airport`]} maxLength={200} onChange={(e) => set(`${prefix}Airport`, e.target.value)} disabled={disabled} />
        </div>
    </Col>
);

const TravelArrangementPanel = ({ tripId, travelerCount, onChanged }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        airRequired: null, hotelRequired: null, rentalRequired: null,
        outboundDepartureDate: "", outboundPart: "morning", outboundAirport: "", outboundTimeRange: "",
        inboundDepartureDate: "", inboundPart: "morning", inboundAirport: "", inboundTimeRange: "",
        rentalMoreThanOne: false, under25: false, miscReservations: "", spellings: [],
    });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const load = useCallback(() => {
        setLoading(true); setError(null);
        api.get(`TravelExpense/${tripId}/arrangement`)
            .then(({ data: raw }) => {
                const d = toCamel(raw);
                setData(d);
                if (!d?.request) setForm((f) => ({ ...f, spellings: Array.isArray(d?.prefillNames) ? d.prefillNames : [] }));
            })
            .catch((e) => setError(e?.response?.data?.error || e?.message || "Failed to load the request"))
            .finally(() => setLoading(false));
    }, [tripId]);
    useEffect(load, [load]);

    const submit = async () => {
        setBusy(true); setError(null); setNotice(null);
        try {
            await api.post(`TravelExpense/${tripId}/arrangement`, {
                ...form,
                outboundDepartureDate: form.airRequired && form.outboundDepartureDate ? form.outboundDepartureDate : null,
                inboundDepartureDate: form.airRequired && form.inboundDepartureDate ? form.inboundDepartureDate : null,
            });
            setNotice("Request successfully submitted. HR has been notified.");
            load();
            onChanged && onChanged();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Request not sent");
        } finally { setBusy(false); }
    };

    const fulfill = async () => {
        if (!window.confirm("Mark all pre-arrangement requests of this trip as fulfilled?")) return;
        setBusy(true); setError(null); setNotice(null);
        try {
            await api.post(`TravelExpense/${tripId}/arrangement/fulfill`);
            setNotice("This request has successfully been marked as completed.");
            load();
            onChanged && onChanged();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Could not mark the request fulfilled");
        } finally { setBusy(false); }
    };

    if (loading && !data) return <div className="text-center py-4"><Spinner color="primary" /></div>;

    const req = data?.request;
    if (req) {
        const spellings = [req.spelling1, req.spelling2, req.spelling3, req.spelling4, req.spelling5,
            req.spelling6, req.spelling7, req.spelling8, req.spelling9, req.spelling10].filter((s) => s != null && s !== "");
        return (
            <div>
                {error && <Alert color="danger">{error}</Alert>}
                {notice && <Alert color="success">{notice}</Alert>}
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    {data.completed
                        ? <Badge color="success" pill>Request completed by {data.completedByName || "HR"}{req.completedDate ? ` on ${fmtDt(req.completedDate)}` : ""}</Badge>
                        : <Badge color="warning" pill>Request submitted by {data.submitByName} on {fmtDt(req.submitDate)} — waiting for HR</Badge>}
                    {data.canFulfill && (
                        <Button color="primary" size="sm" className="ms-auto" disabled={busy} onClick={fulfill}>
                            {busy ? <Spinner size="sm" /> : "Mark all requests as fulfilled"}
                        </Button>
                    )}
                </div>
                <Row className="g-3">
                    <Col md={4}><div className="text-muted small">Air travel</div><div>{yesNo(req.airRequired)}</div></Col>
                    <Col md={4}><div className="text-muted small">Hotel</div><div>{yesNo(req.hotelRequired)}</div></Col>
                    <Col md={4}>
                        <div className="text-muted small">Rental car</div>
                        <div>{yesNo(req.rentalRequired)}
                            {req.rentalRequired && <> — {req.rentalMoreThanOne ? "more than one driver" : "one driver"}{req.under25 ? ", a driver is under 25" : ""}</>}
                        </div>
                    </Col>
                    {req.airRequired && (
                        <>
                            <Col md={6}>
                                <div className="border rounded p-2">
                                    <div className="fw-semibold">Outbound (leave home)</div>
                                    <div>{fmtDate(req.outboundDepartureDate)} · {partOf(req.outboundMorning, req.outboundAfternoon, req.outboundEvening)} · {req.outboundTimeRange || "—"}</div>
                                    <div className="text-muted small">Preferred airport: {req.outboundAirport || "—"}</div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="border rounded p-2">
                                    <div className="fw-semibold">Inbound (return home)</div>
                                    <div>{fmtDate(req.inboundDepartureDate)} · {partOf(req.inboundMorning, req.inboundAfternoon, req.inboundEvening)} · {req.inboundTimeRange || "—"}</div>
                                    <div className="text-muted small">Preferred airport: {req.inboundAirport || "—"}</div>
                                </div>
                            </Col>
                        </>
                    )}
                    <Col md={6}>
                        <div className="text-muted small">Other reservations</div>
                        <div style={{ whiteSpace: "pre-wrap" }}>{req.miscReservations || "—"}</div>
                    </Col>
                    <Col md={6}>
                        <div className="text-muted small">Travelers' names as written on their ID</div>
                        {spellings.length === 0 ? "—" : <ol className="mb-0 ps-3">{spellings.map((s, i) => <li key={i}>{s}</li>)}</ol>}
                    </Col>
                </Row>
            </div>
        );
    }

    if (!data?.canRequest) {
        return (
            <>
                {error && <Alert color="danger">{error}</Alert>}
                <div className="text-muted">No requests have yet been filed.</div>
            </>
        );
    }

    return (
        <div>
            {error && <Alert color="danger">{error}</Alert>}
            {notice && <Alert color="success">{notice}</Alert>}
            <Alert color="info" className="py-2 small">
                Should you need SS White to pay for travel arrangements in advance, please fill out this form.
                This form can only be filled out once for the whole group — please have all details ready before submitting.
                {travelerCount > 1 && " It can only be used if all travelers in your party are on the same itinerary."}
            </Alert>
            <Row className="g-3">
                <Col md={4}><Label className="fw-semibold mb-1">Air travel</Label>
                    <RequiredRadio name="arr-air" value={form.airRequired} onChange={(v) => set("airRequired", v)} disabled={busy} /></Col>
                <Col md={4}><Label className="fw-semibold mb-1">Hotel</Label>
                    <RequiredRadio name="arr-hotel" value={form.hotelRequired} onChange={(v) => set("hotelRequired", v)} disabled={busy} /></Col>
                <Col md={4}><Label className="fw-semibold mb-1">Rental car</Label>
                    <RequiredRadio name="arr-car" value={form.rentalRequired} onChange={(v) => set("rentalRequired", v)} disabled={busy} />
                    {form.rentalRequired === true && (
                        <div className="mt-1">
                            <div className="form-check">
                                <Input type="checkbox" className="form-check-input" id="arr-more" checked={form.rentalMoreThanOne}
                                    onChange={(e) => set("rentalMoreThanOne", e.target.checked)} disabled={busy} />
                                <Label className="form-check-label" for="arr-more">There will be more than one driver</Label>
                            </div>
                            <div className="form-check">
                                <Input type="checkbox" className="form-check-input" id="arr-u25" checked={form.under25}
                                    onChange={(e) => set("under25", e.target.checked)} disabled={busy} />
                                <Label className="form-check-label" for="arr-u25">I am under 25 years of age</Label>
                            </div>
                        </div>
                    )}
                </Col>
                {form.airRequired === true && (
                    <>
                        <Col md={12} className="text-muted small">
                            Please enter all times in the same time zone as the airport — do not convert to our local time zone.
                        </Col>
                        <FlightLeg title="Outbound travel — when should the flight depart to leave home?" prefix="outbound" form={form} set={set} disabled={busy} />
                        <FlightLeg title="Inbound travel — when should the flight depart to return home?" prefix="inbound" form={form} set={set} disabled={busy} />
                    </>
                )}
                <Col md={6}>
                    <Label className="mb-1">Any other reservations required (please specify)</Label>
                    <Input type="textarea" rows={3} value={form.miscReservations} maxLength={2000}
                        onChange={(e) => set("miscReservations", e.target.value)} disabled={busy} />
                </Col>
                <Col md={6}>
                    <Label className="mb-1">Each traveler's name as written on his or her ID (driver license)</Label>
                    {form.spellings.map((s, i) => (
                        <Input key={i} className="mb-1" bsSize="sm" value={s} maxLength={200}
                            onChange={(e) => set("spellings", form.spellings.map((x, j) => (j === i ? e.target.value : x)))} disabled={busy} />
                    ))}
                    <div className="text-muted small">If someone is no longer coming on the trip, write that instead of the name.</div>
                </Col>
            </Row>
            <Button color="primary" className="mt-3" disabled={busy} onClick={submit}>
                {busy ? <Spinner size="sm" /> : "Submit request to HR"}
            </Button>
        </div>
    );
};

export default TravelArrangementPanel;
